import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  CatalogItem,
  getCatalog,
  GetCatalogRequest,
} from "@/modules/learningCenter/features/CourseDirectory/api/getCatalog";
import {
  CatalogMetadata,
  getCatalogMetadata,
} from "@/modules/learningCenter/features/CourseDirectory/api/getCatalogMetadata";

const PAGE_SIZE = 12;
const SEARCH_DEBOUNCE_MS = 300;

export interface CourseDirectoryFilters {
  levels: string[];
  tradeId: number[];
}

const EMPTY_FILTERS: CourseDirectoryFilters = {
  levels: [],
  tradeId: [],
};

// Maps the current search + filter selection to the catalog request. Every
// selected multi-value facet entry is forwarded (no collapsing to the first).
export function buildCatalogRequest(
  search: string,
  filters: CourseDirectoryFilters,
  page: number,
): GetCatalogRequest {
  return {
    search: search.trim() ? search.trim() : undefined,
    levels: filters.levels.length ? filters.levels : undefined,
    tradeId: filters.tradeId.length ? filters.tradeId : undefined,
    page,
    pageSize: PAGE_SIZE,
  };
}

export function useCourseDirectory() {
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);

  const [filters, setFilters] = useState<CourseDirectoryFilters>(EMPTY_FILTERS);

  const [items, setItems] = useState<CatalogItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  // True only until the very first catalog page settles. Drives the full-page
  // skeleton on initial load (subsequent search/filter loads keep the grid and
  // show an inline spinner instead).
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const [metadata, setMetadata] = useState<CatalogMetadata | null>(null);

  // Tracks the in-flight catalog request so a new search/filter/scroll can
  // cancel the previous one before issuing the next.
  const abortRef = useRef<AbortController | null>(null);

  // Load filter facets once on mount.
  useEffect(() => {
    const controller = new AbortController();
    getCatalogMetadata(controller.signal)
      .then((res) => setMetadata(res.data))
      .catch((err) => {
        if (!axios.isCancel(err)) {
          // Global interceptor already surfaced the error toast.
        }
      });
    return () => controller.abort();
  }, []);

  // Fetch a fresh first page whenever the search or filters change. Appends are
  // handled separately by loadMore.
  useEffect(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setPage(1);

    getCatalog(
      buildCatalogRequest(debouncedSearch, filters, 1),
      controller.signal,
    )
      .then((res) => {
        setItems(res.data);
        setHasMore(res.meta.hasMore);
        setTotalCount(res.meta.totalCount);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          setItems([]);
          setHasMore(false);
        }
      })
      .finally(() => {
        // A superseded (cancelled) request must not clear the spinner — the
        // request that replaced it owns the loading state.
        if (!controller.signal.aborted) {
          setIsLoading(false);
          setIsInitialLoading(false);
        }
      });

    return () => controller.abort();
  }, [debouncedSearch, filters]);

  const loadMore = useCallback(() => {
    const nextPage = page + 1;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setPage(nextPage);

    getCatalog(
      buildCatalogRequest(debouncedSearch, filters, nextPage),
      controller.signal,
    )
      .then((res) => {
        setItems((prev) => [...prev, ...res.data]);
        setHasMore(res.meta.hasMore);
        setTotalCount(res.meta.totalCount);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          setHasMore(false);
        }
      });
  }, [page, debouncedSearch, filters]);

  const toggleFilterValue = useCallback(
    <K extends keyof CourseDirectoryFilters>(
      facet: K,
      value: CourseDirectoryFilters[K][number],
    ) => {
      setFilters((prev) => {
        const current = prev[facet] as Array<typeof value>;
        const next = current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value];
        return { ...prev, [facet]: next };
      });
    },
    [],
  );

  const clearFilters = useCallback(() => {
    setSearchInput("");
    setFilters(EMPTY_FILTERS);
  }, []);

  const hasActiveFilters =
    !!searchInput.trim() ||
    filters.levels.length > 0 ||
    filters.tradeId.length > 0;

  return {
    searchInput,
    setSearchInput,
    filters,
    toggleFilterValue,
    clearFilters,
    hasActiveFilters,
    items,
    hasMore,
    totalCount,
    isLoading,
    isInitialLoading,
    loadMore,
    metadata,
  };
}
